import React, { useCallback, useEffect, useState } from "react";
import QRCode from "qrcode";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Button,
    TextField,
    Box,
    Paper,
    Alert,
    Stack,
} from "@mui/material";
import { useFetch } from "./FetchProvider";
import { FormattedMessage, useIntl } from "react-intl";

const baseApiUrl = "/api/user/totp";

type GenerateSecretStageProps = {
    onSuccess: () => void;
};

const GenerateSecretStage = ({ onSuccess }: GenerateSecretStageProps) => {
    const [secret, setSecret] = useState<string | null>(null);
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
    const [verificationCode, setVerificationCode] = useState<string>("");
    const [verificationError, setVerificationError] = useState<string | null>(null);
    const fetch = useFetch();
    const intl = useIntl();

    useEffect(() => {
        const generate = async () => {
            const response = await fetch(baseApiUrl + "/generate-secret", { method: "post" });

            // 500 and above is handled by FetchProvider
            if (response.status !== 200 && response.status < 500) {
                alert("Unexpected error");
                return;
            }

            const data = await response.json();
            setSecret(data.secret);
            setQrCodeDataUrl(await QRCode.toDataURL(data["provisioning_uri"]));
        };

        generate();
    }, [fetch, setSecret, setQrCodeDataUrl]);

    const QRImage = () => {
        if (!secret) return null;

        return (
            <Box
                component="img"
                src={qrCodeDataUrl}
                sx={{
                    border: "1px solid #ccc",
                    borderRadius: 1,
                    mb: 0.5,
                }}
                alt="QR Code"
            />
        );
    };

    const submit = async () => {
        const response = await fetch(baseApiUrl + "/enable", {
            method: "post",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ code: verificationCode }),
        });

        // 500 and above is handled by FetchProvider
        if (response.status !== 200 && response.status < 500) {
            alert("Unexpected error");
            return;
        }

        const data = await response.json();
        setVerificationError(data.error);
        if (!data.error) onSuccess();
    };

    const VerificationErrorText = () => {
        if (!verificationError) return null;

        return (
            <Typography color="error" sx={{ p: "0.3em 0.6em" }}>
                {/* eslint-disable-next-line formatjs/enforce-id, formatjs/enforce-default-message */}
                <FormattedMessage id={`account.totp.generate_stage.code_verification.error.${verificationError}`} />
            </Typography>
        );
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
                <FormattedMessage id="account.totp.generate_stage.title" defaultMessage="Authentication verification" />
            </Typography>
            <Box>
                <Typography sx={{ my: 0.5 }}>
                    <FormattedMessage
                        id="account.totp.generate_stage.image_label"
                        defaultMessage="Scan the image below with two-factor authentication app on your phone"
                    />
                </Typography>
                <Box>
                    <QRImage />
                </Box>
                <Typography sx={{ my: 0.5 }}>
                    <FormattedMessage
                        id="account.totp.generate_stage.code_label"
                        defaultMessage="If you can't use a QR code, enter this text code instead"
                    />
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, fontFamily: "monospace" }}>
                    {secret}
                </Paper>
                <Typography sx={{ my: 0.5 }}>
                    <FormattedMessage
                        id="account.totp.generate_stage.code_verification.label"
                        defaultMessage="Enter the code from the application"
                    />
                </Typography>
                <TextField
                    value={verificationCode}
                    onChange={(ev) => setVerificationCode(ev.target.value)}
                    fullWidth
                    error={!!verificationError}
                    inputProps={{ maxLength: 6 }}
                    placeholder={intl.formatMessage({
                        id: "account.totp.generate_stage.code_verification.placeholder",
                        defaultMessage: "6-digit code",
                    })}
                    sx={{ mb: 1 }}
                />
                <VerificationErrorText />
            </Box>
            <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button disabled={verificationCode.length !== 6} onClick={submit}>
                    <FormattedMessage
                        id="account.totp.generate_stage.submit"
                        defaultMessage="Submit code and enable two-factor authentication"
                    />
                </Button>
            </Box>
        </Box>
    );
};

type DisabledContentProps = {
    onSuccess: () => void;
    initialStage: number;
};

const DisabledContent = ({ onSuccess, initialStage }: DisabledContentProps) => {
    const [stage, setStage] = useState<number>(initialStage);

    const InfoStage = () => (
        <Stack spacing={2}>
            <Alert severity="warning">
                <FormattedMessage
                    id="account.totp.disabled_text"
                    defaultMessage="Two factor authentication is not enabled yet."
                />
            </Alert>
            <Button onClick={() => setStage(1)}>
                <FormattedMessage id="account.totp.enable_button" defaultMessage="Enable two-factor authentication" />
            </Button>
        </Stack>
    );

    switch (stage) {
        case 0:
            return <InfoStage />;
        case 1:
            return <GenerateSecretStage onSuccess={onSuccess} />;
        default:
            return null;
    }
};

type EnabledContentProps = {
    onSuccess: () => void;
    onReconfigure: () => void;
};

const EnabledContent = ({ onSuccess, onReconfigure }: EnabledContentProps) => {
    const fetch = useFetch();

    const disableTOTP = async () => {
        const response = await fetch(baseApiUrl + "/disable", { method: "post" });

        // 500 and above is handled by FetchProvider
        if (response.status !== 200 && response.status < 500) {
            alert("Unexpected error");
            return;
        }

        onSuccess();
    };

    return (
        <Stack spacing={2}>
            <Alert severity="success">
                <FormattedMessage
                    id="account.totp.enabled_text"
                    defaultMessage="Two factor authentication is enabled."
                />
            </Alert>
            <Stack direction="row">
                <Button color="error" onClick={disableTOTP}>
                    <FormattedMessage id="account.totp.disable_button" defaultMessage="Disable" />
                </Button>
                <Button color="error" onClick={onReconfigure}>
                    <FormattedMessage id="account.totp.reconfigure_button" defaultMessage="Reconfigure" />
                </Button>
            </Stack>
        </Stack>
    );
};

type DialogProps = {
    show: boolean;
    onHide: () => void;
};

const OTPDialog = ({ show, onHide }: DialogProps) => {
    const [enabled, setEnabled] = useState(false);
    const [initialDisabledStage, setInitialDisabledStage] = useState(0);
    const fetch = useFetch();

    const fetchData = useCallback(async () => {
        const response = await fetch(baseApiUrl + "/info");
        if (response.status !== 200) {
            alert("Unexpected error");
            return;
        }

        const data = await response.json();
        setEnabled(data.enabled);
    }, [fetch]);

    const Header = () => (
        <DialogTitle>
            <FormattedMessage id="account.totp.settings" defaultMessage="Two factor authentication settings" />
        </DialogTitle>
    );

    const onEnabled = () => {
        setEnabled(true);
    };

    const onDisabled = () => {
        setEnabled(false);
    };

    const onReconfigure = () => {
        setEnabled(false);
        setInitialDisabledStage(1);
    };

    const Body = () => (
        <DialogContent>
            {enabled ? (
                <EnabledContent onSuccess={onDisabled} onReconfigure={onReconfigure} />
            ) : (
                <DisabledContent onSuccess={onEnabled} initialStage={initialDisabledStage} />
            )}
        </DialogContent>
    );

    // Use onEnter and onExited with the Dialog component properly
    useEffect(() => {
        if (show) {
            fetchData();
        }
    }, [fetchData, show]);

    // Reset stage when dialog closes
    useEffect(() => {
        if (!show) {
            setInitialDisabledStage(0);
        }
    }, [show]);

    return (
        <Dialog open={show} onClose={onHide} maxWidth="sm" fullWidth>
            <Header />
            <Body />
        </Dialog>
    );
};

const AccountOTPSettingsDialog = () => {
    const [showDialog, setShowDialog] = useState<boolean>(false);

    console.debug("Showing dialog", showDialog);

    return (
        <Box>
            <Button variant="contained" size="small" onClick={() => setShowDialog(!showDialog)}>
                <FormattedMessage id="account.totp.settings" defaultMessage="Two factor authentication settings" />
            </Button>
            <OTPDialog show={showDialog} onHide={() => setShowDialog(false)} />
        </Box>
    );
};

export default AccountOTPSettingsDialog;
